import { PersonRequest } from "@plinknz/tah-website-elements";

import { ADDRESS_FINDER_KEY } from "../../components/survey-widgets/address-finder-widget";
import { DATEPICKER_KEY } from "../../components/survey-widgets/datepicker-widget";

import { personalDetailsConfig } from "./config-generics/personal-details";
import { signatureConfig } from "./config-generics/signature";
import {
  PageConfig,
  SurveyConfig,
} from "../../components/content-blocks/forms/customer-form-types";

function attendeeConfig({ index }: { index: number }): PageConfig[] {
  const visibleIf = `{attendee${index - 1}Additional} = "Āe"`;
  return [
    {
      name: "attendee",
      title: `Taipitopito - Attendee ${index} Details`,
      visibleIf,
      questions: [
        {
          type: "text",
          name: `attendee${index}Name`,
          title: "Ko wai tōna ingoa?",
          placeHolder: "What is their name?",
          isRequired: true,
        },
        {
          type: DATEPICKER_KEY,
          inputType: "date",
          name: `attendee${index}DOB`,
          title: "What is their date of birth?",
          isRequired: true,
        },
        {
          title: "Īmeera",
          type: "text",
          name: `attendee${index}Email`,
          placeHolder: "What is their email?",
          validators: [
            {
              type: "email",
            },
          ],
          startWithNewLine: true,
          isRequired: true,
        },
      ],
    },
    {
      name: "attendee",
      title: `Taipitopito - Attendee ${index} Contacts`,
      visibleIf,
      questions: [
        {
          type: "text",
          name: `attendee${index}Number`,
          title: "Contact Number",
          placeHolder: "Ko te aha te nama waea?",
        },
        {
          type: ADDRESS_FINDER_KEY,
          name: `attendee${index}Address`,
          title: "Address",
        },
      ],
    },
    {
      name: "hauora",
      title: `Hauora - Attendee ${index} Medical`,
      visibleIf,
      questions: [
        {
          type: "radiogroup",
          name: `attendee${index}Condition`,
          title:
            "Do they have any medical conditions or disabilities that could impact your involvement on this wananga?",
          choices: ["Āe", "Kāo"],
          isRequired: true,
        },
        {
          type: "text",
          name: `attendee${index}ConditionDetails`,
          title:
            "Please list medical conditions. It is important that we are aware of all needs of our whānau that attend our wānanga so we can be accommodating to all. This includes all medical and behavioral information.",
          placeHolder: "Mate",
          visibleIf: `{attendee${index}Condition} = 'Āe'`,
          isRequired: true,
        },
      ],
    },
    {
      name: "matePāwera",
      title: `Mate Pāwera - Attendee ${index} Allergies`,
      visibleIf,
      questions: [
        {
          type: "radiogroup",
          name: `attendee${index}Allergies`,
          title: "Do they have any allergies, including dietary requirements?",
          choices: ["Āe", "Kāo"],
          isRequired: true,
        },
        {
          type: "text",
          name: `attendee${index}AllergyDetails`,
          title:
            "Please list all allergies or dietary requirements, as well as medications required.",
          placeHolder: "Mate Pāwera",
          visibleIf: `{attendee${index}Allergies} = 'Āe'`,
          isRequired: true,
        },
      ],
    },
    {
      name: "additionalAttendee",
      title: "He Hunga Anō - Additional Attendee",
      visibleIf,
      questions: [
        {
          type: "radiogroup",
          name: `attendee${index}Additional`,
          title:
            "Would you like to include another person on your registration?",
          choices: ["Āe", "Kāo"],
          isRequired: true,
        },
      ],
    },
  ];
}

export const createSurveyConfig = (member: PersonRequest): SurveyConfig => {
  const personalConfig = personalDetailsConfig(member);
  const additionalAttendees = [];
  let index = 2;
  while (index < 21) {
    additionalAttendees.push(...attendeeConfig({ index }));

    index += 1;
  }

  return {
    pages: [
      {
        name: "overview",
        title: "Overview - Tirohanga Whānui",
        elements: [
          {
            type: "html",
            html: `
<p>
  This three-day wānanga will be held <strong>Friday 28th – Sunday
  30th November 2025</strong>.
</p>
<p>
  The wananga will be similar to the previous tuna harvest
  and monitoring wānanga, however will also include the
  blessing of the new pouwhenua outside Lake Rotoiti School.
  The main focus of this wānanga is to share knowledge around
  the tuna harvest process and mauri monitoring of the wai
  and surrounding taiao.
</p>
<p>
  To provide you with some insight, below is a brief snapshot
  of what our wānanga will entail: 
  <table style="border-collapse: separate;border-spacing: 15px 10px;">
    <tr>
      <th>Day 1</th>
      <td>Setting of Nets</td>
    </tr>
    <tr>
      <th style="vertical-align: text-top">
        Day 2
      </th>
      <td style="vertical-align: text-top">
        <p style="margin-bottom: 0">
          Blessing and unveiling of pouwhenua at Lake
          Rotoiti School (dawn)
        </p>
        <p style="margin-bottom: 0">
          Harvesting, and processing tuna
        </p>
        <p style="margin-bottom: 0">
          Mauri monitoring
        </p>
      </td>
    </tr>
    <tr>
      <th>Day 3</th>
      <td>Pack-out and travel home</td>
    </tr>
</p>
          `,
          },
        ],
      },
      {
        name: "guidelines",
        title: "Guidelines - Aratohu",
        elements: [
          {
            type: "html",
            html: `
<p>
  Please register for this wānanga by filling out this form.
  This ensures we have accurate numbers for catering and
  accommodation. Please note, you will be responsible for your
  own transport to and from Rotoiti. Unfortunately, we are unable
  to offer any travel subsidies.
</p>
<p>
  Registration will <strong>close Wednesday 19th November 2025</strong>. Late
  registrations will not be accepted.
</p>
<p>
  If you have any pātai, or wish to discuss this form, please
  contact the office
  <a href="mailto:events@rangitane.org.nz" target="_blank">
    events@rangitane.org.nz
  </a> | Telephone
  <a href="tel:+6435786180" target="_blank">
    (03) 578 6180
  </a>.
</p>
          `,
          },
        ],
      },
      ...personalConfig,
      {
        name: "hauora",
        title: "Hauora - Medical",
        questions: [
          {
            type: "radiogroup",
            name: "attendeeCondition",
            title:
              "Do you have any medical conditions or disabilities that could impact your involvement on this wananga?",
            choices: ["Āe", "Kāo"],
            isRequired: true,
          },
          {
            type: "text",
            name: "attendeeConditionDetails",
            title:
              "Please list medical conditions. It is important that we are aware of all needs of our whānau that attend our wānanga so we can be accommodating to all. This includes all medical and behavioral information.",
            placeHolder: "Mate",
            visibleIf: "{attendeeCondition} = 'Āe'",
            isRequired: true,
          },
        ],
      },
      {
        name: "matePāwera",
        title: "Mate Pāwera - Allergies",
        questions: [
          {
            type: "radiogroup",
            name: "attendeeAllergies",
            title: "Do you have any allergies, including dietary requirements?",
            choices: ["Āe", "Kāo"],
            isRequired: true,
          },
          {
            type: "text",
            name: "attendeeAllergyDetails",
            title:
              "Please list all allergies or dietary requirements, as well as medications required.",
            placeHolder: "Mate Pāwera",
            visibleIf: "{attendeeAllergies} = 'Āe'",
            isRequired: true,
          },
        ],
      },
      {
        name: "additionalAttendee",
        title: "He Hunga Anō - Additional Attendee",
        questions: [
          {
            type: "radiogroup",
            name: "attendee1Additional",
            title:
              "Would you like to include another person on your registration?",
            choices: ["Āe", "Kāo"],
            isRequired: true,
          },
        ],
      },
      ...additionalAttendees,
      {
        name: "attendanceDetails",
        title: "Taipitopito Kaupapa - Attendance Details",
        questions: [
          {
            type: "radiogroup",
            name: "attendanceDetails",
            title: "Clarify which events you will be attending",
            choices: [
              "Tuna Wānanga Only",
              "Pouwhenua Blessing Only",
              "Both Events",
            ],
            isRequired: true,
          },
          {
            type: "radiogroup",
            name: "accommodation",
            title: "Do you require accommodation for this wānanga?",
            choices: ["Āe", "Kāo"],
            isRequired: true,
          },
          {
            type: "radiogroup",
            name: "mediaRelease",
            title:
              "During the wananga, we will be taking photos and videos which may be shared on our panui, website and/or our social media platforms. By agreeing with this statement, you give permission for Rangitāne o Wairau to share these photos or videos that may contain images of all those noted on this registration form.",
            choices: ["Āe", "Kāo"],
            isRequired: true,
          },
        ],
      },
      {
        name: "contact1",
        title:
          "Taipitopito Pā Ohotata - Contact details of Emergency Contact #1",
        questions: [
          {
            type: "text",
            name: "contact1Name",
            title: "Name",
            placeHolder: "Ingoa",
            isRequired: true,
          },
          {
            type: "text",
            name: "contact1Relationship",
            title: "Relationship to you",
            placeHolder: "Hononga",
            isRequired: true,
          },
          {
            type: "text",
            name: "contact1Phone",
            title: "Phone",
            placeHolder: "Nama Waea",
            isRequired: true,
          },
        ],
      },
      {
        name: "contact2",
        title:
          "Taipitopito Pā Ohotata - Contact details of Emergency Contact #2",
        questions: [
          {
            type: "text",
            name: "contact2Name",
            title: "Name",
            placeHolder: "Ingoa",
            isRequired: true,
          },
          {
            type: "text",
            name: "contact2Relationship",
            title: "Relationship to you",
            placeHolder: "Hononga",
            isRequired: true,
          },
          {
            type: "text",
            name: "contact2Phone",
            title: "Phone",
            placeHolder: "Nama Waea",
            isRequired: true,
          },
        ],
      },
      {
        name: "privacy",
        title: "Noho Matatapu - Privacy",
        elements: [
          {
            type: "html",
            html: `
<p>
  The information you provide on this form is being collected for
  the purpose of registering you for this event and for managing
  event logistics, communications, and follow-up activities.
</p>
<p>
  Your personal information will be handled in accordance with
  the Privacy Act 2020, which governs how personal data is
  collected, stored, used, and shared. It may be shared with
  event partners (such as funding partners) only where necessary
  for the delivery of the event or to meet our reporting and
  accountability obligations.
</p>
<p>
  Information collected will also form part of our organisational
  records and will be securely managed and retained in line with
  our record-keeping and privacy practices.
</p>
<p>
  You have the right to access and request correction of any
  personal information we hold about you. To do so, or if you
  have any other queries regarding your personal information,
  please contact us at
  <a href="mailto:admin@rangitane.org.nz" target="_blank">
    admin@rangitane.org.nz
  </a>
</p>
`,
          },
        ],
      },
      {
        name: "confirmation",
        title: "Kī Taurangi - Declaration",
        questions: [
          {
            type: "radiogroup",
            name: "physicalWork",
            title:
              "I understand that some activities may involve physical work (e.g., setting nets, processing tuna) and agree to follow all health and safety guidance provided by organisers.",
            choices: ["Āe"],
            isRequired: true,
          },
          signatureConfig[0].questions[0],
        ],
      },
    ],
  };
};
